import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link as RouterLink, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Link,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  InfoOutlined as InfoIcon,
  Sync as SyncIcon,
  Block as BlockIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import facturaCompraService, { FacturaCompra } from '../../services/facturaCompraService';
import variablesSistemaService from '../../services/variablesSistemaService';
import { formatCurrency, formatDateLocal } from '../../utils/formatters';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import VerAsientoContableButton from '../../components/shared/VerAsientoContableButton';
import { getApiErrorMessage } from '../../utils/apiError';
import PDFViewer from '../../components/shared/PDFViewer';
import ImprimirEtiquetasCodigosDialog from '../../components/compras/ImprimirEtiquetasCodigosDialog';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  const s = formatDateLocal(fecha);
  return s || '-';
};

/** Formato Letra-Suc-Nro: A-0001-00000001 */
const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim() || '-';
  const suc = String(nroSuc ?? 0).padStart(4, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

function isSuperAdmin(roles: string[] | undefined): boolean {
  return (roles ?? []).some((r) => String(r).toUpperCase() === 'SUPERADMIN');
}

/** Anulada solo cuando el backend marcó explícitamente Nombre = ANULADO. */
const esFacturaAnulada = (fc: FacturaCompra): boolean =>
  (fc.nombre ?? '').trim().toUpperCase() === 'ANULADO';

const FacturaCompraDetalle: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const superAdmin = isSuperAdmin(user?.roles);
  const [factura, setFactura] = useState<FacturaCompra | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerandoAsiento, setRegenerandoAsiento] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [openRegenerarDialog, setOpenRegenerarDialog] = useState(false);
  const [openImpuestosDialog, setOpenImpuestosDialog] = useState(false);
  const [etiquetasOpen, setEtiquetasOpen] = useState(false);
  const [pdfEtiquetas, setPdfEtiquetas] = useState<{ url: string; fileName: string } | null>(null);
  const pdfEtiquetasRef = useRef<{ url: string; fileName: string } | null>(null);
  const [impuestoTasas, setImpuestoTasas] = useState<{
    impuestotasa1?: number;
    impuestotasa2?: number;
    impuestotasa3?: number;
    impuestotasa4?: number;
  }>({});

  const loadFactura = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (isNaN(id)) {
      enqueueSnackbar('Factura no válida', { variant: 'error' });
      navigate('/compras/facturas');
      return;
    }
    try {
      setLoading(true);
      const data = await facturaCompraService.getFacturaCompra(id);
      setFactura(data);
    } catch (error) {
      console.error('Error al cargar factura:', error);
      enqueueSnackbar('Error al cargar la factura de compra', { variant: 'error' });
      navigate('/compras/facturas');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadFactura();
  }, [loadFactura]);

  useEffect(() => {
    const st = location.state as { pedirEtiquetas?: boolean } | null;
    if (st?.pedirEtiquetas && factura && !loading) {
      setEtiquetasOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [factura, loading, location.pathname, location.state, navigate]);

  useEffect(() => {
    pdfEtiquetasRef.current = pdfEtiquetas;
  }, [pdfEtiquetas]);

  useEffect(() => {
    return () => {
      const p = pdfEtiquetasRef.current;
      if (p?.url) URL.revokeObjectURL(p.url);
    };
  }, []);

  useEffect(() => {
    variablesSistemaService.getImpuestoTasas()
      .then(setImpuestoTasas)
      .catch(() => {});
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!factura) {
    return null;
  }

  const puedeRegenerarAsiento =
    superAdmin && (factura.importeTotal ?? 0) > 0 && !esFacturaAnulada(factura);

  const handleAnular = async () => {
    if (anulando || esFacturaAnulada(factura)) return;
    const id = factura.nroFormulario;
    try {
      const check = await facturaCompraService.puedeAnularFacturaCompra(id);
      if (!check.puedeAnular) {
        enqueueSnackbar(check.motivos[0] ?? 'No se puede anular este comprobante', { variant: 'warning' });
        return;
      }
      if (!window.confirm('¿Está seguro que desea anular esta factura de compra?')) return;

      setAnulando(true);
      await facturaCompraService.anularFacturaCompra(id);
      enqueueSnackbar('Factura de compra anulada correctamente', { variant: 'success' });
      await loadFactura();
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al anular la factura de compra'), { variant: 'error' });
    } finally {
      setAnulando(false);
    }
  };

  const ejecutarRegenerarAsiento = async () => {
    const id = factura.nroFormulario;
    setOpenRegenerarDialog(false);
    setRegenerandoAsiento(true);
    try {
      const r = await facturaCompraService.regenerarAsientoFacturaCompra(id);
      enqueueSnackbar(r.message ?? `Asiento regenerado (Nº ${r.nroAsiento}).`, { variant: 'success' });
      await loadFactura();
      navigate(`/contabilidad/asientos-diario/${r.nroAsiento}`);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { message?: string } } };
      const msg = ax.response?.data?.message ?? 'No se pudo regenerar el asiento.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setRegenerandoAsiento(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/compras/facturas')}
                sx={{ textTransform: 'none' }}
              >
                Facturas de Compra
              </Button>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {String(factura.nroTipoFormulario ?? '').trim() === '2'
                  ? 'Nota de Débito de Compra'
                  : 'Factura de Compra'}{' '}
                {formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {!esFacturaAnulada(factura) && (
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => setEtiquetasOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Imprimir etiquetas
                </Button>
              )}
              {!esFacturaAnulada(factura) && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  disabled={anulando}
                  onClick={() => void handleAnular()}
                >
                  Anular factura
                </Button>
              )}
              {superAdmin && (
                <Tooltip
                  title={
                    puedeRegenerarAsiento
                      ? 'Elimina el/los asiento(s) vinculados y genera uno nuevo con la lógica actual (solo SUPERADMIN).'
                      : 'Se requiere importe total positivo.'
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<SyncIcon />}
                      disabled={!puedeRegenerarAsiento || regenerandoAsiento}
                      onClick={() => setOpenRegenerarDialog(true)}
                    >
                      Regenerar asiento
                    </Button>
                  </span>
                </Tooltip>
              )}
              <VerAsientoContableButton
                nroAsiento={factura.nroAsiento}
                sx={{ borderColor: '#1976d2', color: '#1976d2' }}
              />
            </Stack>
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha emisión</Typography>
              <Typography variant="body1">{formatearFecha(factura.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Nro Comprobante</Typography>
              <Typography variant="body1">{formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha de vencimiento</Typography>
              <Typography variant="body1">{formatearFecha(factura.fechaVencim)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Fecha de aplicación de IVA</Typography>
              <Typography variant="body1">{formatearFecha(factura.aplicacionIva)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Proveedor</Typography>
              <Typography variant="body1">{factura.nroProveedor} - {factura.nombre || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">CUIT</Typography>
              <Typography variant="body1">{factura.cuit || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Forma de pago</Typography>
              <Typography variant="body1">{factura.formaDePago || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Ubicación destino</Typography>
              <Typography variant="body1">
                {factura.nroUbicacionDestino != null
                  ? factura.ubicacionDestinoDescripcion
                    ? `${factura.nroUbicacionDestino} - ${factura.ubicacionDestinoDescripcion}`
                    : String(factura.nroUbicacionDestino)
                  : '-'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Usuario</Typography>
              <Typography variant="body1">{factura.usuario || '-'}</Typography>
            </Grid>
            {(factura.remitosAsociados?.length ?? 0) > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  {(factura.remitosAsociados?.length ?? 0) === 1 ? 'Remito asociado' : 'Remitos asociados'}
                </Typography>
                <Stack spacing={0.5}>
                  {factura.remitosAsociados!.map((r) => (
                    <Link
                      key={r.nroFormulario}
                      component={RouterLink}
                      to={`/compras/remitos/${r.nroFormulario}`}
                      underline="hover"
                      sx={{ color: '#1976d2', fontWeight: 500 }}
                    >
                      {formatoLetraSucNro(r.letra, r.nroSuc, r.nroComprobante ?? undefined)}
                    </Link>
                  ))}
                </Stack>
              </Grid>
            )}
            {factura.descripcion && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Descripción</Typography>
                <Typography variant="body1">{factura.descripcion}</Typography>
              </Grid>
            )}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cantidad</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio lista</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">% Desc</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Precio unit.</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Descuento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(factura.detalles || []).map((d, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {(d.tipoProveedor ?? '').trim().toUpperCase() === 'G' ? 'Concepto' : 'Producto'}
                    </TableCell>
                    <TableCell>{d.codigoArticulo || '-'}</TableCell>
                    <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="right">{Number(d.cantidad)?.toLocaleString('es-AR', { minimumFractionDigits: 2 }) ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioLista ?? 0)}</TableCell>
                    <TableCell align="right">{Number(d.porcDesc)?.toFixed(1) ?? '-'}%</TableCell>
                    <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.descuento ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Totales
          </Typography>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal</Typography>
                  <Typography>{formatCurrency(factura.subtotal ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    Descuento
                    {[factura.porcDesc, factura.porcDesc2, factura.porcDesc3]
                      .filter((p) => p != null && Number(p) > 0).length > 0 && (
                      <> ({[factura.porcDesc, factura.porcDesc2, factura.porcDesc3]
                        .filter((p) => p != null && Number(p) > 0)
                        .map((p) => `${Number(p).toFixed(1)}%`)
                        .join(', ')})
                      </>
                    )}
                  </Typography>
                  <Typography>{formatCurrency(factura.descuento ?? 0)}</Typography>
                </Stack>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Subtotal gravado</Typography>
                  <Typography>{formatCurrency(factura.importeSinIva ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa1 != null ? `${impuestoTasas.impuestotasa1}%` : '1'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivainscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa2 != null ? `${impuestoTasas.impuestotasa2}%` : '2'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivanoInscripto ?? 0)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">
                    IVA {impuestoTasas.impuestotasa3 != null ? `${impuestoTasas.impuestotasa3}%` : '3'}
                  </Typography>
                  <Typography>{formatCurrency(factura.ivaotro ?? 0)}</Typography>
                </Stack>
                {(factura.letra ?? '').trim().toUpperCase() === 'X' ? (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">IVA No categorizado</Typography>
                    <Typography>{formatCurrency(factura.exento ?? 0)}</Typography>
                  </Stack>
                ) : (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography color="text.secondary">Exento</Typography>
                    <Typography>{formatCurrency(factura.exento ?? 0)}</Typography>
                  </Stack>
                )}
                {(factura.percepciones ?? 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      component="span"
                      color="text.secondary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setOpenImpuestosDialog(true)}
                    >
                      Otros impuestos
                      {(factura.impuestos?.length ?? 0) > 0 && (
                        <IconButton size="small" sx={{ ml: 0.5, py: 0 }} onClick={(e) => { e.stopPropagation(); setOpenImpuestosDialog(true); }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                    <Typography>{formatCurrency(factura.percepciones ?? 0)}</Typography>
                  </Stack>
                )}
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  <Typography>Importe total</Typography>
                  <Typography>{formatCurrency(factura.importeTotal ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <NumeroInternoMeta nro={factura.nroFormulario} />
        </CardContent>
      </Card>

      <Dialog open={openRegenerarDialog} onClose={() => setOpenRegenerarDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Regenerar asiento contable</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 0.5 }}>
            Se eliminarán todas las líneas del libro diario vinculadas a esta factura (incluido si el asiento estaba
            contabilizado) y se creará un asiento nuevo con los importes y cuentas según la configuración actual.
            ¿Continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRegenerarDialog(false)} disabled={regenerandoAsiento}>
            Cancelar
          </Button>
          <Button variant="contained" color="warning" onClick={ejecutarRegenerarAsiento} disabled={regenerandoAsiento}>
            {regenerandoAsiento ? 'Procesando…' : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openImpuestosDialog} onClose={() => setOpenImpuestosDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Otros impuestos</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Cuenta</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto base</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Monto impuesto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(factura.impuestos ?? []).map((imp, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{imp.codigoImpuesto ?? '-'}</TableCell>
                    <TableCell align="right">{imp.ctaCtable ?? '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoBaseImp ?? 0)}</TableCell>
                    <TableCell align="right">{formatCurrency(imp.montoImpuesto ?? 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenImpuestosDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <ImprimirEtiquetasCodigosDialog
        open={etiquetasOpen}
        nroFormulario={factura.nroFormulario}
        detalles={factura.detalles}
        textoOrigen="la factura"
        fileName={`etiquetas-fc-${factura.nroFormulario}.pdf`}
        obtenerPdf={(id, items) => facturaCompraService.getEtiquetasCodigosPdf(id, items)}
        onClose={() => setEtiquetasOpen(false)}
        onPdfReady={(url, fileName) => {
          setPdfEtiquetas((prev) => {
            if (prev?.url) URL.revokeObjectURL(prev.url);
            return { url, fileName };
          });
        }}
      />

      {pdfEtiquetas ? (
        <PDFViewer
          pdfUrl={pdfEtiquetas.url}
          title={`Etiquetas factura ${formatoLetraSucNro(factura.letra, factura.nroSuc, factura.nroComprobante ?? undefined)}`}
          documentId={String(factura.nroFormulario)}
          documentType="informeCliente"
          downloadFileName={pdfEtiquetas.fileName}
          onClose={() => {
            URL.revokeObjectURL(pdfEtiquetas.url);
            setPdfEtiquetas(null);
          }}
        />
      ) : null}
    </Box>
  );
};

export default FacturaCompraDetalle;
